/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import HairstyleCard from './HairstyleCard';
import FiltersBar from './FiltersBar';
import hairstylesData from '../../data/hairstyles.json';
import GenderToggle from '../haircutsStudio/GenderToggle';
import { haircutPresets } from '../../data/haircutsStudioPresets';
import { buildHaircutPrompt } from '../../utils/haircutsStudioPromptBuilder';
import { getSuggestionsOptionLabel } from '../../utils/hairstyleSuggestionsI18n';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const SuggestionsPage = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation('admin');
  const [gender, setGender] = useState(() => {
    try {
      const saved = localStorage.getItem('hs_suggestions_gender');
      return saved === 'women' ? 'women' : 'men';
    } catch (e) {
      return 'men';
    }
  });
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({ length: [], texture: [], vibe: [] });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      localStorage.setItem('hs_suggestions_gender', gender);
    } catch (e) {
      // ignore
    }
  }, [gender]);

  // Simulate loading delay for better UX
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const filteredHairstyles = useMemo(() => {
    const hairstyles = (hairstylesData || []).filter(h => (h.gender || 'men') === gender);
    
    const filtered = hairstyles.filter(hairstyle => {
      // Search filter
      const searchTerm = query.trim().toLowerCase();
      const localizedTags = (hairstyle.tags || []).map((tag) =>
        getSuggestionsOptionLabel(tag, t).toLowerCase()
      );
      const matchesSearch = !searchTerm ||
        hairstyle.name.toLowerCase().includes(searchTerm) ||
        hairstyle.overview.toLowerCase().includes(searchTerm) ||
        hairstyle.tags.some(tag => tag.toLowerCase().includes(searchTerm)) ||
        localizedTags.some(tag => tag.includes(searchTerm));

      // Length filter
      const matchesLength = !filters.length.length ||
        filters.length.some(length => hairstyle.tags.includes(length));

      // Texture filter
      const matchesTexture = !filters.texture.length ||
        filters.texture.some(texture => hairstyle.tags.includes(texture));

      // Vibe filter
      const matchesVibe = !filters.vibe.length ||
        filters.vibe.some(vibe => hairstyle.tags.includes(vibe));

      return matchesSearch && matchesLength && matchesTexture && matchesVibe;
    });

    // For MEN suggestions only: show the exact prompt Haircuts Studio would generate
    // (preset style + constraints), so what you see here matches what we actually use.
    if (gender !== 'men') return filtered;

    return filtered.map((hairstyle) => {
      const preset = haircutPresets.find(
        (p) => p?.genderTags?.includes('men') && p?.name === hairstyle.name
      );

      if (!preset) return hairstyle;

      const fullPrompt = buildHaircutPrompt({
        gender: 'men',
        preset,
        colorMode: 'no_change',
        colorValue: null,
        userCustomPrompt: ''
      });

      return {
        ...hairstyle,
        // Keep prompt content "correct" (from Haircuts Studio builder) but normalize typography for display
        // so it doesn't look copy/pasted.
        prompt: String(fullPrompt).replaceAll('—', ' - ')
      };
    });
  }, [gender, query, filters, t]);

  const resultsSubtitle = isLoading
    ? t('admin.suggestions_loading_hairstyles')
    : t(
        filteredHairstyles.length === 1
          ? 'admin.suggestions_results_found_one'
          : 'admin.suggestions_results_found_other',
        { count: filteredHairstyles.length }
      );

  // Skeleton component for loading state
  const SkeletonCard = () => (
    <div className="rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl">
      <div className="aspect-[4/5] rounded-t-2xl animate-pulse bg-gradient-to-r from-white/10 via-white/20 to-white/10 dark:from-zinc-800/20 dark:via-zinc-700/30 dark:to-zinc-800/20" />
      <div className="p-4 md:p-5 space-y-3">
        <div className="h-5 rounded animate-pulse bg-gradient-to-r from-white/10 via-white/20 to-white/10 dark:from-zinc-800/20 dark:via-zinc-700/30 dark:to-zinc-800/20" />
        <div className="h-4 rounded animate-pulse w-3/4 bg-gradient-to-r from-white/10 via-white/20 to-white/10 dark:from-zinc-800/20 dark:via-zinc-700/30 dark:to-zinc-800/20" />
        <div className="flex gap-2">
          <div className="h-6 rounded-full animate-pulse w-16 bg-gradient-to-r from-white/10 via-white/20 to-white/10 dark:from-zinc-800/20 dark:via-zinc-700/30 dark:to-zinc-800/20" />
          <div className="h-6 rounded-full animate-pulse w-20 bg-gradient-to-r from-white/10 via-white/20 to-white/10 dark:from-zinc-800/20 dark:via-zinc-700/30 dark:to-zinc-800/20" />
        </div>
        <div className="h-8 rounded-xl animate-pulse bg-gradient-to-r from-white/10 via-white/20 to-white/10 dark:from-zinc-800/20 dark:via-zinc-700/30 dark:to-zinc-800/20" />
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              {t('admin.suggestions_title')}
            </h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              {t('admin.suggestions_subtitle')}
            </p>
          </div>
          <div className="w-72 max-w-full">
            <GenderToggle
              value={gender}
              onChange={(v) => setGender(v === 'women' ? 'women' : 'men')}
              showOther={false}
              storageKey="hs_suggestions_gender"
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader
          title={t('admin.suggestions_filters_title')}
          subtitle={t('admin.suggestions_filters_subtitle')}
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
            </svg>
          }
        />
        <div className="p-4 md:p-5">
          <FiltersBar
            query={query}
            onQueryChange={setQuery}
            filters={filters}
            onFiltersChange={setFilters}
            gender={gender}
          />
        </div>
      </Card>

      {/* Main Content */}
      <Card>
        <CardHeader
          title={t('admin.suggestions_hairstyles_title')}
          subtitle={resultsSubtitle}
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          }
        />
        <div className="p-4 md:p-5">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          ) : filteredHairstyles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {filteredHairstyles.map(hairstyle => (
                <HairstyleCard key={hairstyle.id} {...hairstyle} gender={gender} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-zinc-600 dark:text-zinc-400">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2 text-zinc-900 dark:text-zinc-100">
                {t('admin.suggestions_no_results_title')}
              </h3>
              <p className="text-sm">
                {t('admin.suggestions_no_results_body')}
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SuggestionsPage;
